import React, { ChangeEvent, useState } from "react";

interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp; // Use this for regex patterns, e.g., for email validation
}

interface TextInputProps {
  name: string;
  label?: string;
  value: string;
  onChange: (newValue: string, inputName: string) => void;
  validationRules?: ValidationRule;
  placeholder?: string;
  className?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

// Validation function (same as before)
const validateInput = (value: string, validationRules?: ValidationRule) => {
  let isValid = true;
  let errorMessage = "";

  if (validationRules) {
    if (validationRules.required && !value) {
      isValid = false;
      errorMessage = "This field is required.";
    }

    if (validationRules.minLength && value.length < validationRules.minLength) {
      isValid = false;
      errorMessage = `Please enter at least ${validationRules.minLength} characters.`;
    }

    if (validationRules.maxLength && value.length > validationRules.maxLength) {
      isValid = false;
      errorMessage = `Please enter no more than ${validationRules.maxLength} characters.`;
    }

    if (validationRules.pattern && !validationRules.pattern.test(value)) {
      isValid = false;
      errorMessage = "Please match the requested format.";
    }
  }

  return { isValid, errorMessage };
};

const TextInput: React.FC<TextInputProps> = ({
  name,
  label,
  value,
  onChange,
  validationRules,
  placeholder = "",
  onKeyDown,
  className = "",
  ...props
}) => {
  const [touched, setTouched] = useState(false);
  const validation = validateInput(value, validationRules);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value, name);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="mb-2 text-sm font-medium text-gray-900 cursor-auto"
        >
          {label}
        </label>
      )}
      <input
        id={name}
        name={name}
        type="text"
        onKeyDown={onKeyDown}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`py-2 px-4 rounded border border-gray-500 ${
          className.includes("text-center") ? "text-center" : ""
        } ${
          validation.isValid && touched ? "border-green-500" : "border-red-500"
        } focus:outline-none focus:ring-2 ${
          validation.isValid ? "focus:ring-green-500" : "focus:ring-red-500"
        }`}
        {...props}
      />
      {!validation.isValid && touched && (
        <span className="text-red-500 text-sm mt-1">
          {validation.errorMessage}
        </span>
      )}
    </div>
  );
};

export default TextInput;
